import { useEffect, useRef, useState } from 'react';
import { TPreviewInvoicePdfRequestBody } from '@qapline/core';
import { usePreviewInvoicePdfMutation } from 'modules/invoicing/api';
import { InvoiceFormSchema } from 'modules/invoicing/types';
import { buildPreviewInvoicePayload } from 'modules/invoicing/utils';

/** How long the form has to sit still before a new preview is rendered on the server. */
const PREVIEW_DEBOUNCE_MS = 700;

export type TUseInvoicePdfPreviewArgs = {
    /** Current form values, or null to leave the preview alone (nothing worth previewing yet). */
    values: InvoiceFormSchema | null;
    /** Set when editing an existing invoice, so unset form fields fall back to the stored ones. */
    invoiceId?: number;
};

/**
 * Keeps a PDF of the current invoice form state in sync with the form. Every change is debounced
 * into one server render, and the resulting blob URL replaces the previous one — the old one is
 * revoked so a long editing session does not pile up blobs in the browser.
 */
export const useInvoicePdfPreview = ({ values, invoiceId }: TUseInvoicePdfPreviewArgs) => {
    const [previewInvoicePdf] = usePreviewInvoicePdfMutation();
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const blobUrlRef = useRef<string | null>(null);
    const [renderedPayload, setRenderedPayload] = useState<string | null>(null);

    // Serializing the payload gives the effect a dependency that only changes when the rendered
    // document would actually differ, so retyping into an unrelated field costs nothing.
    const payload = values ? JSON.stringify(buildPreviewInvoicePayload(values, invoiceId)) : null;

    // Covers the debounce as well as the request, so the form shows it is catching up from the
    // moment a value changes rather than only once the request goes out.
    const isRendering = payload !== null && payload !== renderedPayload;

    useEffect(() => {
        if (!payload) return;

        let isCurrent = true;
        const timeout = setTimeout(async () => {
            try {
                const blob = await previewInvoicePdf(JSON.parse(payload) as TPreviewInvoicePdfRequestBody).unwrap();
                if (!isCurrent) return;

                if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = URL.createObjectURL(blob);
                setBlobUrl(blobUrlRef.current);
            } catch {
                // A preview of a form that is not fillable yet is allowed to fail — whatever was
                // rendered last stays on screen rather than flashing an error at every keystroke.
            } finally {
                // Either way this payload has been dealt with, so stop reporting it as pending.
                if (isCurrent) setRenderedPayload(payload);
            }
        }, PREVIEW_DEBOUNCE_MS);

        return () => {
            isCurrent = false;
            clearTimeout(timeout);
        };
    }, [payload, previewInvoicePdf]);

    useEffect(
        () => () => {
            if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        },
        [],
    );

    return { blobUrl, isRendering };
};
